import { useId, useState } from "react";
import { Pencil, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button, Text, Textarea } from "@athena/ui";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import type { CoordinatorNote } from "../helpers/client";
import { NOTE_MAX_LENGTH, formatContactTime } from "../helpers/client";

interface CoordinatorNoteItemProps {
    note: CoordinatorNote;
    /** Each resolves true once saved; false when it was not, and the page has said why. */
    onEdit: (text: string) => Promise<boolean>;
    onDelete: () => Promise<boolean>;
}

/**
 * One note in the notes card: its text, who wrote it and when, and — for its author or platform
 * staff (`canEdit`, decided by the server) — edit and delete.
 *
 * Deleting is permanent and removes the note for every coordinator, so it asks first and says so.
 */
export function CoordinatorNoteItem({ note, onEdit, onDelete }: CoordinatorNoteItemProps) {
    const { t } = useTranslation();
    const fieldId = useId();
    const [
        editing,
        setEditing,
    ] = useState<boolean>(false);
    const [
        draft,
        setDraft,
    ] = useState<string>(note.text);
    const [
        saving,
        setSaving,
    ] = useState<boolean>(false);
    const [
        confirmingDelete,
        setConfirmingDelete,
    ] = useState<boolean>(false);
    const [
        deleting,
        setDeleting,
    ] = useState<boolean>(false);

    const startEditing = () => {
        setDraft(note.text);
        setEditing(true);
    };

    /** The edit stays open with its text if saving fails, so nothing written is lost. */
    const saveEdit = async () => {
        setSaving(true);
        const saved = await onEdit(draft.trim());
        setSaving(false);
        if (saved) setEditing(false);
    };

    const confirmDelete = async () => {
        setDeleting(true);
        const deleted = await onDelete();
        setDeleting(false);
        if (deleted) setConfirmingDelete(false);
    };

    const unchanged = draft.trim() === note.text;

    return (
        <li className="py-4">
            {editing ? (
                <div className="flex flex-col gap-3">
                    <label htmlFor={fieldId} className="sr-only">
                        {t("admin.alumni.notes.editLabel")}
                    </label>
                    <Textarea
                        id={fieldId}
                        autoFocus
                        value={draft}
                        onChange={(event) => setDraft(event.target.value)}
                        maxLength={NOTE_MAX_LENGTH}
                    />
                    <div className="flex justify-end gap-2">
                        <Button variant="outline" size="sm" className="w-auto" onClick={() => setEditing(false)}>
                            {t("common.actions.cancel")}
                        </Button>
                        <Button
                            size="sm"
                            className="w-auto"
                            disabled={!draft.trim() || unchanged || saving}
                            onClick={() => void saveEdit()}
                        >
                            {saving ? t("admin.alumni.notes.saving") : t("admin.alumni.notes.saveEdit")}
                        </Button>
                    </div>
                </div>
            ) : (
                <div className="flex items-start gap-2">
                    <Text size="sm" className="min-w-0 flex-1 leading-relaxed break-words whitespace-pre-wrap">
                        {note.text}
                    </Text>
                    {note.canEdit ? (
                        <div className="flex shrink-0 gap-1">
                            <Button
                                variant="outline"
                                size="icon-sm"
                                aria-label={t("admin.alumni.notes.edit")}
                                onClick={startEditing}
                            >
                                <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                                variant="outline"
                                size="icon-sm"
                                aria-label={t("admin.alumni.notes.delete")}
                                onClick={() => setConfirmingDelete(true)}
                            >
                                <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                        </div>
                    ) : null}
                </div>
            )}
            <Text size="xs" muted className="mt-2 text-right">
                {note.author ?? t("admin.alumni.notes.removedAuthor")} ·{" "}
                <time dateTime={note.createdAt}>{formatContactTime(note.createdAt)}</time>
                {note.editedAt ? (
                    <>
                        {" · "}
                        <time dateTime={note.editedAt}>
                            {t("admin.alumni.notes.edited", { time: formatContactTime(note.editedAt) })}
                        </time>
                    </>
                ) : null}
            </Text>
            <ConfirmDialog
                open={confirmingDelete}
                onOpenChange={setConfirmingDelete}
                title={t("admin.alumni.notes.deleteTitle")}
                description={t("admin.alumni.notes.deleteDescription")}
                confirmLabel={t("admin.alumni.notes.deleteConfirm")}
                loadingLabel={t("admin.alumni.notes.deleting")}
                loading={deleting}
                onConfirm={() => void confirmDelete()}
            />
        </li>
    );
}
